import React, { useEffect, useMemo, useState, useRef, ChangeEvent, KeyboardEvent } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
    createColumnHelper,
} from "@tanstack/react-table";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import StatCard from "./ReportsCards";

import { getAuthCookie } from "../utils/authCookies";

const DATE_OPTIONS = [
    { label: "Last 7 Days", value: "last_7_days" },
    { label: "Last 15 Days", value: "last_15_days" },
    { label: "Last 30 Days", value: "last_30_days" },
    { label: "Custom", value: "custom" },
];

export const INSTALLATION_JOB_STATUS_OPTIONS = [
    { label: "Scheduled", value: "300" },
    { label: "Completed", value: "500" },
    { label: "Canceled", value: "600" },
];

export const SALES_ORDER_STATUS_OPTIONS = [
    { label: "Confirmed", value: "101" },
    { label: "Canceled", value: "400" },
    { label: "Delivered", value: "401" },
];



const PAYMENT_STATUS_OPTIONS = [
    { label: "All", value: "all" },
    { label: "Paid", value: "Paid" },
    { label: "Partial Payment", value: "Partial Payment" },
    { label: "Pending", value: "Pending" },
    { label: "Overdue", value: "Overdue" },
];

const PAYMENT_METHOD_OPTIONS = [
    { label: "Credit Card", value: "Credit Card" },
    { label: "Net 30", value: "Net 30" },
    { label: "Bank Transfer", value: "Bank Transfer" },
];


interface AgentInstallationOrderRow {
    order_id: string;
    date: string;
    property_address: string;
    status: number;
    supplier: string;
    amount: number;
    paid: number;
    balance: number;
    over_paid: boolean;
    payment_method: string;
}



type SelectOption = { label: string; value: string };

type CardsSummary = {
    total_orders: number;
    completed_orders?: number; // For installation summary
    avg_order_value?: number; // For sales order summary
    amount_paid?: number; // For sales order summary
    outstanding_balance?: number; // For sales order summary
    percent_paid?: number; // For installation summary
    percent_pending?: number; // For installation summary
};

const DropdownIndicator = (props: any) => {
    if (props.selectProps.isDisabled) return null;
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
        </components.DropdownIndicator>
    );
};

const customSelectStyles = {
    indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
    dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
    indicatorSeparator: () => ({ display: "none" }),
    control: (p: any, s: any) => ({
        ...p,
        backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
        cursor: s.isDisabled ? "not-allowed" : "pointer",
        opacity: 1,
    }),
    singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    valueContainer: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
    placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    multiValue: (p: any, s: any) => ({ ...p, backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor }),
    multiValueLabel: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    multiValueRemove: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : p.display }),
    // Fix dropdown menu positioning to appear below the input
    menu: (provided: any) => ({
        ...provided,
        zIndex: 9999,
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
    }),
    menuList: (provided: any) => ({
        ...provided,
        maxHeight: "200px",
    }),
};



export default function AgentSalesOrderBillingReport({ report }: { report: string }) {
    const token = getAuthCookie("sessionToken");
    const membershipId = getAuthCookie("membershipId");
    // Table/filter state
    const [data, setData] = useState<AgentInstallationOrderRow[]>([]);
    const [cards, setCards] = useState<CardsSummary | null>(null);
    const [datePreset, setDatePreset] = useState(DATE_OPTIONS[2]);
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [jobStatus, setJobStatus] = useState<SelectOption[]>([]);
    const [supplier, setSupplier] = useState<SelectOption | null>(null);
    const [paymentStatus, setPaymentStatus] = useState<SelectOption[]>([]);
    const [paymentMethod, setPaymentMethod] = useState<SelectOption[]>([]);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [rowCount, setRowCount] = useState(0);
    const [searchInput, setSearchInput] = useState("");
    const [globalFilter, setGlobalFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [generateClicked, setGenerateClicked] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    
    // Calculate date range limits (last 6 months from today)
    const dateRangeLimits = useMemo(() => {
        const today = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        
        // Format as YYYY-MM-DD for date input
        const formatDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        return {
            minDate: formatDate(sixMonthsAgo),
            maxDate: formatDate(today)
        };
    }, []);

    // Validate and reset dates when custom is selected or dates are out of range
    useEffect(() => {
        if (datePreset.value === "custom") {
            const minDate = dateRangeLimits.minDate;
            const maxDate = dateRangeLimits.maxDate;
            
            // Reset From date if it's outside the valid range
            if (customFrom && (customFrom < minDate || customFrom > maxDate)) {
                setCustomFrom("");
            }
            
            // Reset To date if it's outside the valid range
            if (customTo && (customTo < minDate || customTo > maxDate)) {
                setCustomTo("");
            }
        }
    }, [datePreset.value, dateRangeLimits, customFrom, customTo]);

    // Dropdown options
    const [brokerageOptions, setBrokerageOptions] = useState([]);
    const [officeOptions, setOfficeOptions] = useState([]);
    const [agentOptions, setAgentOptions] = useState([]);
    const [supplierOptions, setSupplierOptions] = useState([]);

    const location = useLocation();
    const hasShownToast = useRef(false);

    useEffect(() => {
        if (location.state?.successMessage && !hasShownToast.current) {
            toast.success(location.state.successMessage);
            hasShownToast.current = true;
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    // Fetch dropdown options for brokerage, office, agent (replace with your APIs)
    // useEffect(() => {
    //     // Fetch brokerageOptions
    //     const fetchBrokerageFirms = async () => {
    //         try {
    //             const response = await fetch(
    //                 `${process.env.REACT_APP_API_URL}/users-search?created_by.id=${userId}&licenseType=BROKERAGE_FIRM`,
    //                 {
    //                     method: "GET",
    //                     headers: {
    //                         Authorization: `Bearer ${token}`,
    //                         "Content-Type": "application/json",
    //                     },
    //                 }
    //             );

    //             const data = await response.json();
    //             // console.log("fetchBrokerageFirms data :", data);

    //             if (!response.ok) {
    //                 // throw new Error("Failed to fetch agents");
    //                 toast.error(data.error || "Failed to fetch firms");
    //             }

    //             setBrokerageOptions(data.map((i: any) => ({ label: `${i.firstName} ${i.lastName}`, value: i.id })));
    //             // console.log("Brokerage Firms:", data);
    //         } catch (err) {
    //             setError(
    //                 err instanceof Error ? err.message : "An unknown error occurred"
    //             );
    //             console.error("Error fetching brokerage firms:", err);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };
    //     fetchBrokerageFirms()
    // }, []);

    useEffect(() => {
        const fetchSuppliersByUserId = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/agent/suppliers/${membershipId}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch supplier list");
                }

                const data = await response.json();
                console.log("Suppliers:", data);
                setSupplierOptions(data.map((i: any) => ({ label: i.name, value: i.organization_id })));
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "An unknown error occurred"
                );
            } finally {
                setLoading(false);
            }
        };
        fetchSuppliersByUserId();
    }, []);


    // Fetch data when Generate Button is clicked
    useEffect(() => {
        if (generateClicked) {
            fetchData();
        }
        else {
            fetchData();
        }
        // eslint-disable-next-line
    }, [generateClicked, pagination, globalFilter]);

    // Fetch data when report type changes
    useEffect(() => {
        // Reset filters when report type changes
        setDatePreset(DATE_OPTIONS[2]); // Last 30 Days
        setCustomFrom("");
        setCustomTo("");
        setJobStatus([]);
        setSupplier(null);
        setPaymentStatus([]);
        setPaymentMethod([]);
        setGlobalFilter("");
        setSearchInput("");
        setPagination({ pageIndex: 0, pageSize: 10 });

        // Clear existing data and trigger fetch
        setData([]);
        setCards(null);
        setRowCount(0);
        setGenerateClicked(true);
    }, [report]);

    function handleGenerate() {
        setGlobalFilter(searchInput.trim());
        setPagination({ ...pagination, pageIndex: 0 });
        setGenerateClicked(true);
    }

    // Function to clear all filters
    function handleClearFilters() {
        setDatePreset(DATE_OPTIONS[2]); // Last 30 Days
        setCustomFrom("");
        setCustomTo("");
        setJobStatus([]);
        setSupplier(null);
        setPaymentStatus([]);
        setPaymentMethod([]);
        setGlobalFilter("");
        setSearchInput("");
        setPagination({ pageIndex: 0, pageSize: 10 });
        setCards(null);
        setGenerateClicked(true);
    }

    async function fetchData() {
        setLoading(true);
        const endpoint = report === "installation_summary" ? "reports/agent/installation_orders_summary" : "reports/agent/sales_orders_summary";
        const res = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({
                page: pagination.pageIndex + 1,
                limit: pagination.pageSize,
                agent_membership_id: membershipId,
                status: jobStatus.map((s) => Number(s.value)),
                seller_org_id: supplier?.value,
                payment_status: paymentStatus.map((s) => s.value),
                payment_method: paymentMethod.map((p) => p.value),
                search: globalFilter, // Add search functionality
                date_range: {
                    type: datePreset.value,
                    from: datePreset.value === "custom" ? customFrom : "",
                    to: datePreset.value === "custom" ? customTo : "",
                },
            }),
        });
        const result = await res.json();
        setData(result.data || []);
        setCards(result.cards || null);
        setRowCount(result.total || 0);
        setLoading(false);
        setGenerateClicked(false);
    }

    // Table columns
    const columnHelper = createColumnHelper<AgentInstallationOrderRow>();
    const columns = useMemo(() => [
        columnHelper.display({
            id: "sno",
            header: () => <div className="th-text S-No">S.No.</div>,
            cell: (info) => (
                <div className="text-sm S-No">
                    {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
                </div>
            ),
        }),
        columnHelper.accessor("order_id", {
            header: () => <div className="th-text">Order ID</div>,
            cell: (info) => <div className="fw-medium text-sm">{info.getValue()}</div>,
        }),
        columnHelper.accessor("date", {
            header: () => <div className="th-text">Date</div>,
            cell: (info) =>
                <div className="text-sm">
                    {info.getValue() ? new Date(info.getValue() as string).toLocaleDateString() : "--"}
                </div>,
        }),
        ...(report === "installation_summary"
            ? [
                columnHelper.accessor("property_address", {
                    header: () => <div className="th-text">Property Address</div>,
                    cell: (info) => (
                        <div className="text-sm">{info.getValue() || "--"}</div>
                    ),
                }),
                columnHelper.accessor("status", {
                    header: () => <div className="th-text">Status</div>,
                    cell: (info) => (
                        <div className="text-sm">{installationMapStatusToText(info.getValue())}</div>
                    ),
                }),
            ]
            : [
                columnHelper.accessor("status", {
                    header: () => <div className="th-text">Status</div>,
                    cell: (info) => (
                        <div className="text-sm">{salesOrderMapStatusToText(info.getValue())}</div>
                    ),
                }),
            ]
        ),
        columnHelper.accessor("supplier", {
            header: () => <div className="th-text">Supplier</div>,
            cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
        }),
        columnHelper.accessor("amount", {
            header: () => <div className="th-text">Amount</div>,
            cell: (info) =>
                <div className="fw-medium text-sm">
                    ${typeof info.getValue() === "number" ? info.getValue().toFixed(2) : "--"}
                </div>,
        }),
        columnHelper.accessor("paid", {
            header: () => <div className="th-text">Paid</div>,
            cell: (info) =>
                <div className="text-sm" style={{ color: "#00a63e" }}>
                    ${typeof info.getValue() === "number" ? info.getValue().toFixed(2) : "--"}
                </div>,
        }),
        columnHelper.accessor("balance", {
            header: () => <div className="th-text">Balance</div>,
            cell: (info) => {
                const overPaid = info.row.original.over_paid;
                const value = info.getValue();
                const balance = typeof value === "number" ? value : 0;
                return (
                    <div className={`text-sm fw-medium`} style={{ color: overPaid ? "#00a63e" : "#e7000b" }}>
                        {overPaid ? `+$${balance.toFixed(2)}` : `-$${balance.toFixed(2)}`}
                    </div>
                );
            },
        }),
        // columnHelper.accessor("payment_method", {
        //     header: () => <div className="th-text">Payment Method</div>,
        //     cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
        // }),
    ], [pagination, report]);



    const table = useReactTable({
        data: data || [],
        columns,
        pageCount: Math.ceil(rowCount / pagination.pageSize),
        state: { pagination },
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        onPaginationChange: setPagination,
    });

    // Helper for status code to text
    function installationMapStatusToText(status: number) {
        switch (status) {
            case 200: return "Confirmed";
            case 300: return "Scheduled";
            case 500: return "Completed";
            case 600: return "Canceled";
            default: return "--";
        }
    }

    function salesOrderMapStatusToText(status: number) {
        switch (status) {
            // Draft & Confirmation
            case 100: return "Draft";
            case 101: return "Confirmed";

            // Terminal
            case 400: return "Canceled";
            case 401: return "Completed";

            default: return "--";
        }
    }




    return (
        <div className="content">
            {/* Header */}
            <div className="mb-4">
                <div>
                    <h4 className="mb-1 po-title">
                        {report === "installation_summary"
                            ? "Installation Orders Summary Report"
                            : "Sales Orders Summary Report"
                        }
                    </h4>
                    <p className="po-subtitle">
                        {report === "installation_summary"
                            ? "Track installation orders and billing status for agents"
                            : "Track sales orders and billing status for agents"
                        }
                    </p>
                </div>
            </div>

            {/* Stat Cards */}
            <div className="row g-3 mb-4">
                <div className="col-lg-3">
                    <StatCard
                        label="Total Orders"
                        value={cards?.total_orders ?? 0}
                        money={false}
                        accent="blue"
                        icon="ri-settings-3-line"
                    />
                </div>

                <div className="col-lg-3">
                    <StatCard
                        label={report === "installation_summary" ? "Completed Orders" : "Average Order Value"}
                        value={report === "installation_summary" 
                            ? (cards?.completed_orders ?? 0) 
                            : (cards?.avg_order_value ?? 0)}
                        money={report !== "installation_summary"}
                        accent="green"
                        icon="bi-graph-up-arrow"
                    />
                </div>

                <div className="col-lg-3">
                    <StatCard
                        label={report === "installation_summary" ? "% Paid" : "Total Paid"}
                        value={report === "installation_summary" 
                            ? (cards?.percent_paid ?? 0) 
                            : (cards?.amount_paid ?? 0)}
                        money={report !== "installation_summary"}
                        accent="green"
                        icon="bi-currency-dollar"
                    />
                </div>

                <div className="col-lg-3">
                    <StatCard
                        label={report === "installation_summary" ? "% Pending" : "Total Balance"}
                        value={report === "installation_summary" 
                            ? (cards?.percent_pending ?? 0) 
                            : (cards?.outstanding_balance ?? 0)}
                        money={report !== "installation_summary"}
                        accent="red"
                        icon="bi-exclamation-octagon"
                    />
                </div>
            </div>

            {/* Filters */}
            <div className="po-header mb-4 shadow-border-datatables">
                <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                    <div className="po-title-wrap me-2" />
                    <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
                        {/* Search */}
                        <div className="input-group po-search">
                            <span className="input-group-text">
                                <i className="ri-search-line" />
                            </span>
                            <input
                                type="text"
                                id="searchInput"
                                name="searchInput"
                                className="form-control"
                                placeholder={report === "installation_summary" ? "Search by Order ID,Property Address, Supplier ..." : "Search by Order ID, Supplier ..."}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleGenerate();
                                }}
                            />
                        </div>

                        {/* Filters toggle */}
                        <button
                            type="button"
                            className={`po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
                            onClick={() => setFiltersOpen((v) => !v)}
                            aria-expanded={filtersOpen}
                            aria-controls="agent-filters"
                        >
                            <i className="ri-filter-3-line me-2" />
                            <span>Filters</span>
                            <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
                        </button>
                    </div>
                </div>

                <div id="agent-filters" className={`po-filters ${filtersOpen ? "show" : ""}`} style={{ overflow: "visible" }}>
                    <div className="po-filters-bar d-flex" style={{ overflow: "visible" }}>
                        <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto" style={{ overflow: "visible" }}>
                            <div className="col-lg-2" style={{ position: "relative" }}>
                                <label className="form-label mb-1 label-1">Date Range</label>
                                <Select
                                    options={DATE_OPTIONS}
                                    value={datePreset}
                                    onChange={(v) => v && setDatePreset(v)}
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                />
                            </div>

                            {datePreset.value === "custom" && (
                                <>
                                    <div className="col-lg-2">
                                        <label className="form-label mb-1 label-1">From</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={customFrom}
                                            onChange={(e) => setCustomFrom(e.target.value)}
                                            min={dateRangeLimits.minDate}
                                            max={dateRangeLimits.maxDate}
                                        />
                                    </div>
                                    <div className="col-lg-2">
                                        <label className="form-label mb-1 label-1">To</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={customTo}
                                            onChange={(e) => setCustomTo(e.target.value)}
                                            min={dateRangeLimits.minDate}
                                            max={dateRangeLimits.maxDate}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="col-lg-2" style={{ position: "relative" }}>
                                <label className="form-label mb-1 label-1">Job Status</label>
                                <Select
                                    isMulti
                                    options={report === "installation_summary" ? INSTALLATION_JOB_STATUS_OPTIONS : SALES_ORDER_STATUS_OPTIONS}
                                    value={jobStatus}
                                    onChange={(val) => setJobStatus(val as SelectOption[])}
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                />
                            </div>

                            <div className="col-lg-2" style={{ position: "relative" }}>
                                <label className="form-label mb-1 label-1">Supplier</label>
                                <Select
                                    options={supplierOptions}
                                    value={supplier}
                                    onChange={(val) => setSupplier(val || null)}
                                    placeholder="Select Supplier"
                                    isClearable
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                />
                            </div>

                            <div className="col-lg-2" style={{ position: "relative" }}>
                                <label className="form-label mb-1 label-1">Payment Status</label>
                                <Select
                                    isMulti
                                    options={PAYMENT_STATUS_OPTIONS}
                                    value={paymentStatus}
                                    onChange={(val) => setPaymentStatus(val as SelectOption[] || [])}
                                    isClearable
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                />
                            </div>

                            <div className="col-lg-auto d-flex align-items-end gap-2">
                                <button className="btn btn-primary table-cells table-addbutton br-8" onClick={handleGenerate}>
                                    Apply Filter
                                </button>
                                <button className="btn back-btn surface-btn fw-bold fs-16 table-cells" onClick={handleClearFilters}>
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
                <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
                    <div className="d-flex align-items-center">
                        <i className="ri-file-text-line me-2" aria-hidden="true" style={{ fontSize: 18, color: "#2563eb", lineHeight: 1 }} />
                        <h5 className="mb-0">
                            {report === "installation_summary" ? "Installation Orders Data" : "Sales Orders Data"}
                        </h5>
                    </div>
                </div>
                <hr className="mb-2" style={{ borderTop: "none" }} />

                {/* Desktop / Tablet */}
                <div className="rdt-desktop">
                    <div className="production-scroll" style={{ "--rdt-min": "1200px" } as React.CSSProperties}>
                        <table className="table production-table align-middle mb-0 table-1">
                            <DesktopTableParts table={table} />
                        </table>
                    </div>
                </div>

                {/* Mobile */}
                <div className="rdt-mobile">
                    <MobileCardsOnly
                        table={table}
                        idColId="order_id"
                        titleColId="property_address"
                        actionColId="amount"
                    />
                </div>

                {/* Loading indicator */}
                {loading && data.length === 0 && (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2 text-muted">Loading {report === "installation_summary" ? "installation orders" : "sales orders"}...</div>
                    </div>
                )}

                {/* No data message */}
                {!loading && data.length === 0 && (
                    <div className="text-center py-4 text-muted">
                        No {report === "installation_summary" ? "installation orders" : "sales orders"} found
                    </div>
                )}
            </div>

            <div className="mt-4 mb-4">
                <TablePaginationControls table={table} />
            </div>
        </div>
    )
}
